import {
  installRuntimeConfig,
  type RuntimeConfig,
} from './runtime-config';

interface FederationImportMap {
  readonly imports?: Readonly<Record<string, string>>;
}

export interface PortalBootstrapDependencies {
  readonly itemManagerRemoteEntry?: string;
  /** When true, legacy routes stay on even if `/runtime-config.json` says off. */
  readonly buildTimeLegacyRoutesEnabled?: boolean;
  readonly loadRuntimeConfig: (url: string) => Promise<RuntimeConfig>;
  readonly initFederation: (remotes: Record<string, string>) => Promise<unknown>;
  readonly markItemManagerRemoteReady: (ready: boolean) => void;
  readonly importBootstrap: () => Promise<unknown>;
  readonly warn: (message: string) => void;
  readonly error: (error: unknown) => void;
}

function remoteRuntimeConfigUrl(remoteEntry: string): string | null {
  try {
    return new URL('runtime-config.json', remoteEntry).toString();
  } catch {
    return null;
  }
}

function hasItemManagerImports(info: unknown): boolean {
  const imports = (info as FederationImportMap | undefined)?.imports ?? {};
  return Object.keys(imports).some(
    (key) => key === 'item-manager' || key.startsWith('item-manager/'),
  );
}

function resolveLocalLegacyConfig(
  fetched: RuntimeConfig,
  buildTimeLegacyRoutesEnabled?: boolean,
): RuntimeConfig {
  if (buildTimeLegacyRoutesEnabled) {
    return Object.freeze({ itemManagementLegacyRoutes: true, valid: true });
  }
  return fetched;
}

export async function bootstrapPortal(
  dependencies: PortalBootstrapDependencies,
): Promise<void> {
  const fetchedLocalConfig = await dependencies.loadRuntimeConfig('/runtime-config.json');
  const localConfig = resolveLocalLegacyConfig(
    fetchedLocalConfig,
    dependencies.buildTimeLegacyRoutesEnabled,
  );
  installRuntimeConfig(localConfig);

  const remotes: Record<string, string> = {};
  const remoteEntry = dependencies.itemManagerRemoteEntry;
  let legacyRequested = false;

  if (localConfig.valid && localConfig.itemManagementLegacyRoutes && remoteEntry) {
    legacyRequested = true;
    const configUrl = remoteRuntimeConfigUrl(remoteEntry);
    if (!configUrl) {
      dependencies.warn(
        '[federation] item-manager remote URL is invalid; the remote stays disabled.',
      );
    } else {
      const remoteConfig = await dependencies.loadRuntimeConfig(configUrl);
      if (remoteConfig.valid && !remoteConfig.itemManagementLegacyRoutes) {
        dependencies.warn(
          '[federation] item-manager remote runtime-config disables legacy, but the portal build mounts it anyway.',
        );
      }
      remotes['item-manager'] = remoteEntry;
    }
  }

  try {
    const info = await dependencies.initFederation(remotes);
    const loaded = Boolean(remotes['item-manager']) && hasItemManagerImports(info);
    dependencies.markItemManagerRemoteReady(loaded);

    if (legacyRequested && !loaded && remotes['item-manager']) {
      dependencies.warn(
        `[federation] item-manager remote did not load from ${remotes['item-manager']}; legacy routes stay unavailable.`,
      );
    }
  } catch (error) {
    dependencies.markItemManagerRemoteReady(false);
    dependencies.error(error);
  }

  await dependencies.importBootstrap();
}
